/** Piezas chicas compartidas por el tablero, el drawer y los diálogos de tareas. */
import { currentLocale } from '@/lib/locale';
import { todayLocal } from '@/../../shared/dates';
import { Check } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useTaskAssignees } from '@/hooks/useInternalTasks';
import { useTranslation } from 'react-i18next';
import {
  PROJECT_COLORS,
  TASK_PRIORITIES,
  TASK_PRIORITY_COLOR,
  TASK_PRIORITY_LABEL,
  TASK_STATUSES,
  TASK_STATUS_COLOR,
  TASK_STATUS_LABEL,
  type Task,
  type TaskPriority,
  type TaskStatus,
} from '@/../../shared/internal-tasks';

export function Dot({ color, className }: { color: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('inline-block h-2.5 w-2.5 shrink-0 rounded-full', className)}
      style={{ backgroundColor: color }}
    />
  );
}

export function StatusSelect({ value, onChange, className }: {
  value: TaskStatus; onChange: (v: TaskStatus) => void; className?: string;
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as TaskStatus)}>
      <SelectTrigger className={cn('h-8', className)}><SelectValue /></SelectTrigger>
      <SelectContent>
        {TASK_STATUSES.map((s) => (
          <SelectItem key={s} value={s}>
            <span className="flex items-center gap-2"><Dot color={TASK_STATUS_COLOR[s]} />{TASK_STATUS_LABEL[s]}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function PrioritySelect({ value, onChange, className }: {
  value: TaskPriority; onChange: (v: TaskPriority) => void; className?: string;
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as TaskPriority)}>
      <SelectTrigger className={cn('h-8', className)}><SelectValue /></SelectTrigger>
      <SelectContent>
        {TASK_PRIORITIES.map((p) => (
          <SelectItem key={p} value={p}>
            <span className="flex items-center gap-2"><Dot color={TASK_PRIORITY_COLOR[p]} />{TASK_PRIORITY_LABEL[p]}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const UNASSIGNED = '__unassigned__';

/** Responsable: solo usuarios admin (Cognito). `null` = sin asignar. */
export function AssigneeSelect({ value, onChange, className }: {
  value: string | null | undefined; onChange: (email: string | null) => void; className?: string;
}) {
  const { t } = useTranslation();
  const { data: assignees = [] } = useTaskAssignees();
  const current = value ?? UNASSIGNED;
  // Si el responsable ya no es admin, igual lo mostramos para no "perder" el valor.
  const known = assignees.some((a) => a.email === value);
  return (
    <Select value={current} onValueChange={(v) => onChange(v === UNASSIGNED ? null : v)}>
      <SelectTrigger className={cn('h-8', className)}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={UNASSIGNED}>{t('internal.task-fields.sin_asignar')}</SelectItem>
        {value && !known && <SelectItem value={value}>{value}</SelectItem>}
        {assignees.map((a) => (
          <SelectItem key={a.email} value={a.email}>{a.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function ColorPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {PROJECT_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          aria-label={c}
          onClick={() => onChange(c)}
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-full ring-offset-background transition',
            value === c && 'ring-2 ring-ring ring-offset-2'
          )}
          style={{ backgroundColor: c }}
        >
          {value === c && <Check className="h-3.5 w-3.5 text-white" />}
        </button>
      ))}
    </div>
  );
}


/** 'YYYY-MM-DD' → '25 ago' (parse manual: new Date(str) lo toma como UTC y corre un día). */
export function fmtDueDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(currentLocale(), { day: 'numeric', month: 'short' });
}

export const isOverdue = (task: Pick<Task, 'dueDate' | 'status'>) =>
  !!task.dueDate && task.status !== 'completed' && task.dueDate < todayLocal();

/** Nombre corto del responsable para la card (parte local del mail). */
export const shortName = (email: string) => email.split('@')[0];
